import React from 'react';
import type { FC } from 'react';
import { TextInput, useAsyncCallback } from '@wix/cli-ui-kit';
import { useTranslation } from '@wix/cli-i18n';
import { SetNamespaceResult, useDevCenterClient } from '@wix/dev-center-client';
import { isType } from 'variant';
import { useCreateAppBi } from '../bi/index.js';
import { validateNamespace } from '../validations/index.js';

interface ChooseNamespaceProps {
  onSubmit: (namespace: string) => void;
  appId: string;
  packageName: string;
}

export const ChooseNamespace: FC<ChooseNamespaceProps> = ({
  onSubmit,
  appId,
  packageName,
}) => {
  const { t } = useTranslation();
  const { cliFlowStepAnswered } = useCreateAppBi();
  const devCenterClient = useDevCenterClient();
  const validationFn = useAsyncCallback(
    async (_, submitedNamespace: string) => {
      const isValid = validateNamespace(submitedNamespace);
      if (isValid !== true) {
        return isValid;
      }

      const result = await devCenterClient.setNamespace({
        appId,
        namespace: submitedNamespace,
      });

      if (isType(result, SetNamespaceResult.ShouldBeUnique)) {
        return `Namespace ${submitedNamespace} is already used. Try another.`;
      }

      onSubmit(result.namespace);

      return true;
    }
  );

  return (
    <TextInput
      label={t('create_app.choose_namespace.label')}
      placeholder={t('create_app.choose_namespace.placeholder', {
        suggested: packageName,
      })}
      validate={(submitedNamespace) => validationFn.execute(submitedNamespace)}
      onSubmit={(namespace) => {
        cliFlowStepAnswered({
          question: t('create_app.choose_namespace.label'),
          questionKey: 'create_app.choose_namespace.label',
          answer: namespace,
        });
      }}
      inCreateFlow
    />
  );
};
